/**
 * Configurazione e servizi per WooCommerce REST API
 */

// Configurazione WooCommerce
export const WOOCOMMERCE_CONFIG = {
  baseUrl: import.meta.env.VITE_WC_BASE_URL || 'https://retrobottega.braceesapori.it',
  apiPath: '/wp-json/wc/v3',
  consumerKey: import.meta.env.VITE_WC_CONSUMER_KEY || '',
  consumerSecret: import.meta.env.VITE_WC_CONSUMER_SECRET || '',
  checkoutUrl: import.meta.env.VITE_WC_CHECKOUT_URL || 'https://retrobottega.braceesapori.it/checkout',
  woopaymentsPublicKey: import.meta.env.VITE_WOOPAYMENTS_PUBLIC_KEY || '',
};

/**
 * Tipi per WooCommerce API
 */
export interface WooCommerceProduct {
  id: number;
  name: string;
  slug: string;
  permalink: string;
  date_created: string;
  date_modified: string;
  type: string;
  status: string;
  featured: boolean;
  catalog_visibility: string;
  description: string;
  short_description: string;
  sku: string;
  price: string;
  regular_price: string;
  sale_price: string;
  date_on_sale_from: string | null;
  date_on_sale_to: string | null;
  on_sale: boolean;
  purchasable: boolean;
  total_sales: number;
  virtual: boolean;
  downloadable: boolean;
  downloads: Array<{ id: string; name: string; file: string }>;
  download_limit: number;
  download_expiry: number;
  external_url: string;
  button_text: string;
  tax_status: string;
  tax_class: string;
  manage_stock: boolean;
  stock_quantity: number | null;
  stock_status: string;
  backorders: string;
  backorders_allowed: boolean;
  backordered: boolean;
  sold_individually: boolean;
  weight: string;
  dimensions: {
    length: string;
    width: string;
    height: string;
  };
  shipping_required: boolean;
  shipping_taxable: boolean;
  shipping_class: string;
  shipping_class_id: number;
  reviews_allowed: boolean;
  average_rating: string;
  rating_count: number;
  related_ids: number[];
  upsell_ids: number[];
  cross_sell_ids: number[];
  parent_id: number;
  purchase_note: string;
  categories: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  tags: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  images: Array<{
    id: number;
    src: string;
    name: string;
    alt: string;
  }>;
  attributes: Array<{ id: number; name: string; options: string[] }>;
  default_attributes: Array<{ id: number; name: string; option: string }>;
  variations: number[];
  grouped_products: number[];
  menu_order: number;
  meta_data: Array<{ id: number; key: string; value: unknown }>;
  _links: {
    self: Array<{ href: string }>;
    collection: Array<{ href: string }>;
  };
}

export interface WooCommerceCategory {
  id: number;
  name: string;
  slug: string;
  parent: number;
  description: string;
  display: string;
  image: {
    id: number;
    src: string;
    name: string;
    alt: string;
  } | null;
  menu_order: number;
  count: number;
  _links: {
    self: Array<{ href: string }>;
    collection: Array<{ href: string }>;
  };
}

export interface WooCommerceProductsResponse {
  products: WooCommerceProduct[];
  total: number;
  totalPages: number;
}

/**
 * Converte un prodotto WooCommerce nel formato utilizzato dall'app
 */
export const mapWooCommerceProduct = (wcProduct: WooCommerceProduct) => {
  const firstImage = wcProduct.images?.[0]?.src || '/placeholder.svg';
  const category = wcProduct.categories?.[0]?.name || 'Prodotti';
  
  return {
    id: wcProduct.id.toString(),
    name: wcProduct.name,
    description: wcProduct.short_description || wcProduct.description,
    price: parseFloat(wcProduct.price || wcProduct.regular_price || '0'),
    image: firstImage,
    category: category,
    inStock: wcProduct.stock_status === 'instock' && wcProduct.purchasable,
    weight: wcProduct.weight ? `${wcProduct.weight} kg` : undefined,
    origin: 'Puglia', // Puoi aggiungere questo come meta field in WooCommerce
    wcProduct: wcProduct, // Mantieni riferimento al prodotto originale
  };
};

/**
 * Usa proxy backend se disponibile, altrimenti usa direttamente WooCommerce API
 */
const USE_PROXY = import.meta.env.VITE_USE_PROXY === 'true';
const PROXY_ENDPOINT = import.meta.env.VITE_PROXY_ENDPOINT || '/wp-json/braceesapori/v1';

/**
 * Crea l'URL base per le chiamate API WooCommerce
 * Se USE_PROXY è true, usa il proxy backend che nasconde le credenziali
 */
const getApiUrl = (endpoint: string, params?: Record<string, string | number>) => {
  // Se il proxy è abilitato, usa l'endpoint proxy (senza credenziali nell'URL)
  if (USE_PROXY) {
    const url = new URL(`${WOOCOMMERCE_CONFIG.baseUrl}${PROXY_ENDPOINT}${endpoint}`);
    
    // Aggiungi solo i parametri della richiesta (NON le credenziali)
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value.toString());
      });
    }
    
    return url.toString();
  }
  
  // Fallback: usa direttamente WooCommerce API (con credenziali nell'URL)
  const url = new URL(`${WOOCOMMERCE_CONFIG.baseUrl}${WOOCOMMERCE_CONFIG.apiPath}${endpoint}`);
  
  // Aggiungi credenziali come query string (richiesto da WooCommerce)
  if (WOOCOMMERCE_CONFIG.consumerKey && WOOCOMMERCE_CONFIG.consumerSecret) {
    url.searchParams.append('consumer_key', WOOCOMMERCE_CONFIG.consumerKey);
    url.searchParams.append('consumer_secret', WOOCOMMERCE_CONFIG.consumerSecret);
  }
  
  // Aggiungi altri parametri
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value.toString());
    });
  }
  
  return url.toString();
};

/**
 * Crea le opzioni di fetch con autenticazione
 * WooCommerce supporta sia Basic Auth che query string
 * Usiamo query string per compatibilità, ma le aggiungiamo solo all'URL finale
 */
const getFetchOptions = (method: string = 'GET', body?: unknown): RequestInit => {
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  return options;
};

/**
 * Recupera la lista dei prodotti
 * Usa il proxy se disponibile, altrimenti chiama direttamente WooCommerce
 */
export const fetchProducts = async (params?: {
  page?: number;
  per_page?: number;
  category?: number;
  search?: string;
  orderby?: string;
  order?: 'asc' | 'desc';
}): Promise<WooCommerceProductsResponse> => {
  // Se il proxy è abilitato, usa l'API proxy
  if (USE_PROXY) {
    const { productsApi } = await import('./api');
    const products = await productsApi.getProducts(params);
    
    // Converti la risposta nel formato atteso
    return {
      products: Array.isArray(products) ? products : [],
      total: 0, // Il proxy restituirà gli header X-WP-Total
      totalPages: 0,
    };
  }

  const queryParams: Record<string, string | number> = {
    page: params?.page || 1,
    per_page: params?.per_page || 20,
    status: 'publish',
  };

  if (params?.category) {
    queryParams.category = params.category;
  }

  if (params?.search) {
    queryParams.search = params.search;
  }

  if (params?.orderby) {
    queryParams.orderby = params.orderby;
    queryParams.order = params.order || 'asc';
  }

  const response = await fetch(
    getApiUrl('/products', queryParams),
    getFetchOptions('GET')
  );
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('Errore API WooCommerce:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      error: errorText,
    });
    throw new Error(`Errore nel recupero prodotti: ${response.status} ${response.statusText}`);
  }

  const products = await response.json();
  const total = parseInt(response.headers.get('X-WP-Total') || '0', 10);
  const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1', 10);

  return {
    products,
    total,
    totalPages,
  };
};

/**
 * Recupera un singolo prodotto per ID
 */
export const fetchProduct = async (id: number): Promise<WooCommerceProduct> => {
  const response = await fetch(
    getApiUrl(`/products/${id}`),
    getFetchOptions('GET')
  );
  
  if (!response.ok) {
    throw new Error(`Errore nel recupero prodotto: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Recupera le categorie
 */
export const fetchCategories = async (params?: {
  page?: number;
  per_page?: number;
  parent?: number;
}): Promise<WooCommerceCategory[]> => {
  const queryParams: Record<string, string | number> = {
    page: params?.page || 1,
    per_page: params?.per_page || 100,
  };

  if (params?.parent !== undefined) {
    queryParams.parent = params.parent;
  }

  const response = await fetch(
    getApiUrl('/products/categories', queryParams),
    getFetchOptions('GET')
  );
  
  if (!response.ok) {
    throw new Error(`Errore nel recupero categorie: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Interfaccia per i dati del cliente nel checkout
 */
export interface CheckoutCustomerData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: {
    address1: string;
    address2?: string;
    city: string;
    postcode: string;
    state: string;
    country: string;
  };
  note?: string;
}

/**
 * Interfaccia per creare un ordine WooCommerce
 */
export interface CreateOrderData {
  customer: CheckoutCustomerData;
  lineItems: Array<{
    product_id: number;
    quantity: number;
  }>;
  paymentMethod: string;
  paymentMethodTitle: string;
  setPaid?: boolean;
}

/**
 * Interfaccia per la risposta della creazione ordine
 */
export interface WooCommerceOrder {
  id: number;
  status: string;
  currency: string;
  date_created: string;
  total: string;
  payment_url?: string;
  payment_method_title: string;
  billing: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address_1: string;
    address_2: string;
    city: string;
    postcode: string;
    state: string;
    country: string;
  };
  shipping: {
    first_name: string;
    last_name: string;
    address_1: string;
    address_2: string;
    city: string;
    postcode: string;
    state: string;
    country: string;
  };
  line_items: Array<{
    id: number;
    name: string;
    product_id: number;
    quantity: number;
    total: string;
  }>;
}

/**
 * Crea un ordine WooCommerce
 */
export const createOrder = async (orderData: CreateOrderData): Promise<WooCommerceOrder> => {
  const orderPayload = {
    payment_method: orderData.paymentMethod,
    payment_method_title: orderData.paymentMethodTitle,
    set_paid: orderData.setPaid || false,
    billing: {
      first_name: orderData.customer.firstName,
      last_name: orderData.customer.lastName,
      email: orderData.customer.email,
      phone: orderData.customer.phone,
      address_1: orderData.customer.address.address1,
      address_2: orderData.customer.address.address2 || '',
      city: orderData.customer.address.city,
      postcode: orderData.customer.address.postcode,
      state: orderData.customer.address.state,
      country: orderData.customer.address.country,
    },
    shipping: {
      first_name: orderData.customer.firstName,
      last_name: orderData.customer.lastName,
      address_1: orderData.customer.address.address1,
      address_2: orderData.customer.address.address2 || '',
      city: orderData.customer.address.city,
      postcode: orderData.customer.address.postcode,
      state: orderData.customer.address.state,
      country: orderData.customer.address.country,
    },
    line_items: orderData.lineItems,
    customer_note: orderData.customer.note || '',
  };

  const response = await fetch(
    getApiUrl('/orders'),
    getFetchOptions('POST', orderPayload)
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Errore nella creazione dell'ordine: ${response.statusText} - ${errorText}`);
  }

  return response.json();
};

/**
 * Ottiene l'URL di pagamento per un ordine WooCommerce
 * Questo può essere usato per WooPayments o altri gateway di pagamento
 */
export const getOrderPaymentUrl = async (orderId: number): Promise<string> => {
  const order = await fetch(
    getApiUrl(`/orders/${orderId}`),
    getFetchOptions('GET')
  );
  
  if (!order.ok) {
    throw new Error(`Errore nel recupero dell'ordine: ${order.statusText}`);
  }

  const orderData: WooCommerceOrder = await order.json();
  
  // Se WooPayments ha già generato un payment_url, usalo
  if (orderData.payment_url) {
    return orderData.payment_url;
  }

  // Altrimenti, costruisci l'URL di pagamento standard WooCommerce
  return `${WOOCOMMERCE_CONFIG.baseUrl}/checkout/order-pay/${orderId}/?pay_for_order=true&key=${orderData.id}`;
};

/**
 * Verifica lo stato di un ordine
 */
export const getOrderStatus = async (orderId: number): Promise<WooCommerceOrder> => {
  const response = await fetch(
    getApiUrl(`/orders/${orderId}`),
    getFetchOptions('GET')
  );
  
  if (!response.ok) {
    throw new Error(`Errore nel recupero dello stato dell'ordine: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Aggiunge un prodotto al carrello WooCommerce tramite AJAX
 * Nota: Questo richiede che WooCommerce sia configurato per accettare richieste AJAX
 */
export const addToWooCommerceCart = async (productId: number, quantity: number = 1): Promise<void> => {
  // WooCommerce usa endpoint AJAX per aggiungere al carrello
  const url = `${WOOCOMMERCE_CONFIG.baseUrl}/?wc-ajax=add_to_cart`;
  
  const formData = new FormData();
  formData.append('product_id', productId.toString());
  formData.append('quantity', quantity.toString());

  try {
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include', // Importante per i cookie di sessione
      headers: {
        // Alcuni WooCommerce richiedono questo header
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Errore nell'aggiunta al carrello: ${response.statusText} - ${errorText}`);
    }

    // Verifica che la risposta sia valida
    const result = await response.json();
    if (result.error) {
      throw new Error(result.error);
    }
  } catch (error) {
    // Se l'endpoint AJAX non funziona, prova con l'URL diretto
    // Questo è un fallback per WooCommerce che potrebbe non supportare AJAX
    console.warn('AJAX add to cart fallito, uso metodo alternativo:', error);
    
    // Metodo alternativo: usa l'URL diretto di WooCommerce
    const directUrl = `${WOOCOMMERCE_CONFIG.baseUrl}/cart/?add-to-cart=${productId}&quantity=${quantity}`;
    
    // Per prodotti multipli, dobbiamo fare richieste separate o usare un form
    // In questo caso, facciamo una richiesta GET che aggiunge al carrello
    const directResponse = await fetch(directUrl, {
      method: 'GET',
      credentials: 'include',
      mode: 'no-cors', // Permette la richiesta cross-origin
    });
    
    // Con no-cors non possiamo verificare la risposta, ma il prodotto dovrebbe essere aggiunto
    // Se questo metodo non funziona, l'utente dovrà aggiungere manualmente
  }
};

